/**
 * The shell, as an object.
 *
 * Decision 0014 says the demo's own chrome is a lie: its header, pool pills and
 * controls are hand-written HTML sitting beside a generated layout, while the
 * product claims there is no privileged application chrome. This file starts
 * paying that back with the piece that most obviously wants to be a panel — an
 * outline of what is on screen — and makes it a *node in the same spec*,
 * compiled by the same compiler and rendered by the same renderer as the
 * content beside it.
 *
 * The point is not that a sidebar appears. It is that nothing about it is
 * special: it is a collection with a placement, and moving it to another edge
 * is an edit to data, not a branch in a stylesheet.
 *
 * What is deliberately NOT here: any pixel. Where the panel sits is a
 * `placement` from the closed algebra; how wide the user dragged it is view
 * state and lives in `@morph/runtime`'s `view-state.ts`. Decision 0014 draws
 * that line and this file is on the spec side of it.
 */
import type { TLayoutSpec, TNode, DockEdge, Attachment } from '@morph/spec';
import { ACTIONS } from './actions.js';

/** What the user picked for the outline panel. `off` is not a placement — it
 *  means the panel is not in the spec at all, which is different from an
 *  `inline` panel sitting in the flow. */
export type ShellChoice =
  | { readonly kind: 'off' }
  | { readonly kind: 'docked'; readonly edge: DockEdge }
  | { readonly kind: 'floating'; readonly rest: Attachment };

export const SHELL_KEY = 'morph.demo.shell.v1';

/** On by default. The panel is the visible proof of decision 0014 — a shell
 *  that is an object like any other — and a capability nobody switches on is
 *  indistinguishable from one that does not exist. */
export const DEFAULT_CHOICE: ShellChoice = { kind: 'docked', edge: 'inline-end' };

export const SHELL_OPTIONS: readonly { value: string; label: string; choice: ShellChoice }[] = [
  { value: 'off',             label: 'No panel',        choice: { kind: 'off' } },
  { value: 'inline-start',    label: 'Panel: start',    choice: { kind: 'docked', edge: 'inline-start' } },
  { value: 'inline-end',      label: 'Panel: end',      choice: { kind: 'docked', edge: 'inline-end' } },
  { value: 'block-start',     label: 'Panel: top',      choice: { kind: 'docked', edge: 'block-start' } },
  { value: 'block-end',       label: 'Panel: bottom',   choice: { kind: 'docked', edge: 'block-end' } },
  { value: 'float-end',       label: 'Panel: floating', choice: { kind: 'floating', rest: 'inline-end' } },
  { value: 'float-center',    label: 'Panel: bubble',   choice: { kind: 'floating', rest: 'center' } },
];

export function readShellChoice(
  storage: Pick<Storage, 'getItem'> = localStorage,
): ShellChoice {
  try {
    const stored = storage.getItem(SHELL_KEY);
    if (stored === null) return DEFAULT_CHOICE;
    const found = SHELL_OPTIONS.find((o) => o.value === stored);
    // An unrecognised stored value is treated as NO preference rather than as
    // the default, because the value is user-editable and matching it against
    // the offered options is what keeps it from being a way in.
    return found ? found.choice : { kind: 'off' };
  } catch {
    // Private mode throws on access, not just on write. An unreadable
    // preference is not an error worth surfacing — it is simply no preference.
    return { kind: 'off' };
  }
}

export function writeShellChoice(
  value: string, storage: Pick<Storage, 'setItem'> = localStorage,
): void {
  try { storage.setItem(SHELL_KEY, value); } catch { /* private mode */ }
}

/**
 * The outline panel itself.
 *
 * A collection over the same objects the lens is showing, bound to `title`. It
 * is deliberately the plainest node that could work: the argument being made is
 * about placement, and a panel that needed its own node kind would be making
 * the opposite argument.
 */
function outline(): TNode {
  return {
    kind: 'collection',
    id: 'shell-outline',
    flow: 'stack',
    // SIBLINGS, not the subject. Without this the panel listed a single row the
    // moment you opened an object — it is a collection inside the same spec as
    // the content, so it rendered whatever the content rendered. A sidebar that
    // answers "what is here" must not shrink to one when you open something;
    // Obsidian's file list does not empty when you open a note.
    source: 'siblings',
    affordances: ['select', 'navigate'],
    items: [{
      spec: {
        kind: 'card',
        id: 'shell-outline-row',
        affordances: ['select'],
        bindings: [{ slot: 'name', role: 'title', direction: 'read' }],
      },
    }],
  } as unknown as TNode;
}

/**
 * A button, as a node.
 *
 * One card per action, because an invocation belongs to a node and a node has
 * one. `bindings: []` because the button binds no value — its label is derived
 * from the effects it declares, so there is nothing to read off an object.
 *
 * The effects are declared HERE, in the spec, and the registry refuses to run
 * an action that exceeds them. Two declarations that must agree is the point:
 * the spec's is the ceiling, because the spec is the thing that gets reviewed,
 * rendered and pooled.
 */
function button(id: string, action: string, effects: readonly string[]): TNode {
  return {
    kind: 'card',
    id,
    bindings: [],
    affordances: ['invoke'],
    invocation: { action, effects },
  } as unknown as TNode;
}

/** The panel: what you can do, then what is here. */
function panel(placement: Record<string, unknown>): TNode {
  return {
    kind: 'region',
    id: 'shell-panel',
    flow: 'stack',
    placement,
    children: [
      button('shell-new', ACTIONS.create, ['create-object']),
      button('shell-seen', ACTIONS.markSeen, ['set-property']),
      button('shell-open', ACTIONS.openRelated, ['navigate']),
      outline(),
    ],
  } as unknown as TNode;
}

/**
 * Put the shell around a generated lens.
 *
 * The generated spec's root becomes a child rather than being modified, for the
 * reason 0014 gives about the pool: the lens the generator produced is the
 * thing the taste model learns from, and quietly rewriting it would teach the
 * pool a layout nobody chose. The shell wraps; it does not edit.
 *
 * Returns the spec unchanged when the panel is off, so the no-panel path is
 * byte-identical to the one that existed before this file — which is what keeps
 * `candidateId`'s content hash, and every grant minted from it, stable.
 */
export const SHELL_ROOT_ID = 'shell-surface';

export function withShell(spec: TLayoutSpec, choice: ShellChoice): TLayoutSpec {
  if (choice.kind === 'off') return spec;
  // Already wrapped. A drop is applied to the spec the user is LOOKING at, which
  // is the wrapped one, and that spec then becomes the accepted lens — so the
  // next render would wrap it again, and again, one surface per repaint.
  if ((spec.root as { id?: string }).id === SHELL_ROOT_ID) return spec;

  const placement = choice.kind === 'docked'
    ? { mode: 'docked', edge: choice.edge, extent: 'compact', collapsible: true }
    : { mode: 'floating', rest: choice.rest, extent: 'compact', collapsible: true };

  return {
    ...spec,
    root: {
      kind: 'region',
      id: SHELL_ROOT_ID,
      flow: 'stack',
      children: [panel(placement), spec.root],
    },
  } as TLayoutSpec;
}

/**
 * The control that moves it.
 *
 * Built here rather than in `index.html` so that the one hand-written thing
 * this file is arguing against does not grow by another row. It mirrors the
 * existing `.controls label` markup, which is what the rest of the demo's
 * selects already use.
 */
export function mountShellControl(
  host: Element, current: string, onChange: (value: string) => void,
): void {
  if (host.querySelector('#shellPlace')) return;   // idempotent: `run()` is re-entrant
  const label = document.createElement('label');
  label.textContent = 'Shell';
  const select = document.createElement('select');
  select.id = 'shellPlace';
  select.innerHTML = SHELL_OPTIONS
    .map((o) => `<option value="${o.value}"${o.value === current ? ' selected' : ''}>`
      + `${o.label}</option>`)
    .join('');
  select.addEventListener('change', () => onChange(select.value));
  label.appendChild(select);
  host.appendChild(label);
}
